import { useCallback, useMemo } from 'react';
import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ApiError, api, apiUpload } from '@/lib/api-client';
import { useMyAccess as useMeQuery } from '@/api/authz';
import { useAuthStore } from '@/store/auth';

/**
 * The access-control admin API (/api/authz/*) for the Access control and Approval rules pages.
 * Field names are the API's own (backend/app/api/authz.py, authz_groups.py, authz/schemas.py).
 */

export type Effect = 'ALLOW' | 'DENY';
export type GroupSource = 'UKNOWVA' | 'LOCAL' | 'BUILTIN';

/** A row of GET /authz/groups: a flat list, children linked by parent_id. */
export type AuthzGroup = {
  id: string;
  source: GroupSource;
  /** Built-in groups only: everyone, employees. */
  key: string | null;
  title: string;
  description: string | null;
  parent_id: string | null;
  path: string;
  children_count: number;
  /** Linked and pending members together; null for built-in groups, whose members are worked out per person. */
  member_count: number | null;
  /** Of `member_count`: uKnowva usernames in the group not yet linked to a Payroll account. Absent from servers that predate it. */
  pending_count?: number;
  /** What the group's own assignments give; the name comes with the list so readers without authz.role.view see words. Absent from servers that predate it. */
  roles: { key: string; name?: string; effect: Effect }[];
  read_only: boolean;
  is_admin_group: boolean;
  is_super_group: boolean;
  has_rule: boolean;
  updated_at: string;
};

/** A member row; pending ones (a uKnowva username not yet linked to a Payroll account) have no user_id. */
export type GroupMember = {
  user_id: string | null;
  username: string | null;
  name: string | null;
  external_user: string | null;
  origin: 'MANUAL' | 'SYNC' | 'SSO' | 'RULE';
  valid_to: string | null;
};

export type GroupMembersPage = { total: number; items: GroupMember[] };

export type GroupCreate = { title: string; description?: string | null; parent_id?: string | null };
export type GroupPatch = { title?: string; description?: string | null; parent_id?: string | null };

/** The report of POST /authz/groups/members/import, for a dry run and for an applied import alike. */
export type MembersImportReport = {
  rows: { line: number; username: string; added: string[]; errors: string[] }[];
  summary: { rows: number; ok: number; errors: number };
};

export type Grant = {
  permission_key: string;
  effect: Effect;
  ui_mode: 'HIDE' | 'DISABLE' | 'MASK' | null;
  condition: Record<string, unknown> | null;
};

export type AuthzRole = {
  id: string;
  key: string;
  name: string;
  description: string | null;
  is_system: boolean;
  is_super: boolean;
  extends_id: string | null;
  created_at: string;
  /** Sent back as If-Match on edits. */
  updated_at: string;
  holder_count: number;
  grants: (Grant & { edited: boolean })[];
};

export type RoleCreate = { key: string; name: string; description?: string | null; extends_id?: string | null };
export type RolePatch = { name?: string; description?: string | null; extends_id?: string | null };

/** GET /authz/roles/{id}/holders: who holds the role, and through what. */
export type RoleHolder = { user_id: string; username: string; name: string; via: string };

export type Assignment = {
  id: string;
  subject_type: 'USER' | 'GROUP';
  user_id: string | null;
  group_id: string | null;
  role_id: string;
  effect: Effect;
  scope_id: string | null;
  /** Read-only here: entity limits take effect in a later release. */
  entity_id: string | null;
  include_subgroups: boolean;
  valid_from: string | null;
  valid_to: string | null;
  origin: string;
  note: string | null;
  pending_external_title: string | null;
  created_at: string;
  updated_at: string;
  warning: string | null;
};

/** No entity limit on purpose: an entity-limited ALLOW grants nothing at route gates until phase 5. */
export type AssignmentCreate = {
  subject_type: 'USER' | 'GROUP';
  user_id?: string;
  group_id?: string;
  role_id: string;
  effect: Effect;
  valid_from?: string | null;
  valid_to?: string | null;
};

export type AssignmentFilter = { role_id?: string; group_id?: string; user_id?: string; origin?: string };

export type CatalogPermission = {
  key: string;
  label: string;
  verb: string;
  risk: string;
  scopable: boolean;
  is_orphan: boolean;
  /** Only a super role may hold it. Absent from servers that predate the flag, which then lock nothing. */
  super_only?: boolean;
};

export type CatalogModule = { module: string; permissions: CatalogPermission[] };

/** A row of GET /authz/verbs, in the order the grid shows actions. */
export type Verb = { verb: string; risk: string };

/** [scope_id, entity_id, role key, via, valid_to] of one assignment's contribution to an allowed key. */
export type EffectiveAllow = [string | null, string | null, string, string, string | null];

/** GET /authz/effective/{user_id}: the user's compiled grant map with their roles and groups. */
export type Effective = {
  version: number;
  user_id: string;
  is_super: boolean;
  is_active: boolean;
  /** uKnowva blocked the account; it is turned away like an inactive one. */
  uknowva_blocked: boolean;
  roles: { key: string; name: string; via: string }[];
  /** `key` names a built-in group (everyone, employees) and is null for every other. */
  groups: { id: string; title: string; source: GroupSource; key: string | null }[];
  group_ids: string[];
  allow: Record<string, EffectiveAllow[]>;
  allow_wild: [string, EffectiveAllow[]][];
  deny: string[];
  deny_wild: string[];
  entity_ids: string[] | null;
  /** Key or pattern → [role key, via] of the grant that denied it. */
  deny_sources: Record<string, [string, string]>;
  super_roles: string[];
};

export type Explain = {
  allowed: boolean;
  reason: string;
  message: string;
  trace: { step: string; detail: string }[];
};

export type AuditItem = {
  id: number;
  at: string;
  actor_id: string | null;
  actor_label: string;
  action: string;
  target_type: string;
  target_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  ip: string | null;
  authz_version: number;
};

export type AuditParams = {
  target_type?: string;
  target_id?: string;
  /** Changes made by this person. */
  actor_id?: string;
  /** An exact action name, or several separated by commas. */
  action?: string;
  /** Changes made to this person's own account, assignments and group memberships, by anyone. */
  about_user_id?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
};

/** Who holds a permission (GET /authz/permissions/{key}/holders); super users are counted, not listed. */
export type Holders = {
  roles: { id: string; key: string; name: string; via_wildcard: boolean }[];
  groups: { id: string; title: string; source: GroupSource; role_keys: string[] }[];
  users: { id: string; username: string; full_name: string }[];
  super_count: number;
};

/** A row of GET /authz/approval-actions. */
export type ApprovalAction = {
  key: string;
  label: string;
  module: string;
  risk: string;
  /** In-app path of the page the action is taken on; empty when it has no single page. */
  page: string;
  reach_labels: string[];
  extra: { key: string; label: string }[];
  holders: Holders;
};

// Any access write can change groups' role chips, holder counts, effective access and approval holders,
// so every write refreshes the whole authz cache rather than guessing which views it touched.
function refreshAccess(qc: QueryClient) {
  return qc.invalidateQueries({ queryKey: ['authz'] });
}

// ── Me ─────────────────────────────────────────────────────────────────────

/** What the signed-in user may do, by permission key (never by role name). Shares the shell's /authz/me cache. */
export function useMyAccess(): { can: (key: string) => boolean; loading: boolean } {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const me = useMeQuery(userId, true);
  const held = useMemo(() => new Set(me.data?.permissions ?? []), [me.data]);
  const can = useCallback((key: string) => held.has(key), [held]);
  return { can, loading: me.isLoading };
}

// ── Groups ─────────────────────────────────────────────────────────────────

/** Same key as `useAuthzGroups` (@/api/authz): one endpoint, one cache entry. */
export function useGroups() {
  return useQuery({
    queryKey: ['authz', 'groups'],
    queryFn: () => api.get<AuthzGroup[]>('/authz/groups'),
  });
}

export function groupMembersQuery(id: string, page: { limit: number; offset: number }) {
  return queryOptions({
    queryKey: ['authz', 'group-members', id, page],
    queryFn: () => api.get<GroupMembersPage>(`/authz/groups/${id}/members`, page),
  });
}

export function useGroupMembers(id: string | null, page = { limit: 50, offset: 0 }) {
  return useQuery({ ...groupMembersQuery(id ?? '', page), enabled: !!id });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GroupCreate) => api.post<AuthzGroup>('/authz/groups', body),
    onSuccess: () => refreshAccess(qc),
  });
}

/** `updated_at` goes as If-Match, so a group changed by someone else since it was loaded is refused (412). */
export function usePatchGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; updated_at: string; body: GroupPatch }) =>
      api.patch<AuthzGroup>(`/authz/groups/${vars.id}`, vars.body, vars.updated_at),
    onSuccess: () => refreshAccess(qc),
    // A 412 means the cached updated_at is stale; reload the list so a retry sends the current one.
    onError: (err) => (err instanceof ApiError && err.status === 412
      ? qc.invalidateQueries({ queryKey: ['authz', 'groups'] }) : undefined),
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`/authz/groups/${id}`),
    onSuccess: () => refreshAccess(qc),
  });
}

export function useAddMembers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { groupId: string; user_ids: string[]; valid_to?: string | null }) =>
      api.post<{ added: number }>(`/authz/groups/${vars.groupId}/members`,
        { user_ids: vars.user_ids, valid_to: vars.valid_to ?? null }),
    onSuccess: () => refreshAccess(qc),
  });
}

export function useRemoveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { groupId: string; userId: string }) =>
      api.delete<{ deleted: boolean }>(`/authz/groups/${vars.groupId}/members/${vars.userId}`),
    onSuccess: () => refreshAccess(qc),
  });
}

/** `apply: false` checks the file and writes nothing; the report has the same shape either way. */
export function useImportMembers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { file: File; apply: boolean }) => {
      const fd = new FormData();
      fd.append('file', vars.file);
      return apiUpload<MembersImportReport>('/authz/groups/members/import', fd, { apply: vars.apply });
    },
    onSuccess: (_, vars) => (vars.apply ? refreshAccess(qc) : undefined),
  });
}

/** Super users only; refused while uKnowva still manages blocks, since the next sync would block the person again. */
export function useUnblockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.post<{ user_id: string; uknowva_blocked: boolean }>(`/authz/users/${userId}/unblock`),
    onSuccess: () => refreshAccess(qc),
  });
}

// ── Roles ──────────────────────────────────────────────────────────────────

export function rolesQuery() {
  return queryOptions({
    queryKey: ['authz', 'roles'],
    queryFn: () => api.get<AuthzRole[]>('/authz/roles'),
  });
}

/** `enabled`: false for a reader without authz.role.view, so the page does not ask for what it would be refused. */
export function useRoles(enabled = true) {
  return useQuery({ ...rolesQuery(), enabled });
}

export function roleQuery(id: string) {
  return queryOptions({
    queryKey: ['authz', 'role', id],
    queryFn: () => api.get<AuthzRole>(`/authz/roles/${id}`),
  });
}

export function useRole(id: string | null) {
  return useQuery({ ...roleQuery(id ?? ''), enabled: !!id });
}

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RoleCreate) => api.post<AuthzRole>('/authz/roles', body),
    onSuccess: () => refreshAccess(qc),
  });
}

export function useCloneRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; key: string; name: string }) =>
      api.post<AuthzRole>(`/authz/roles/${vars.id}/clone`, { key: vars.key, name: vars.name }),
    onSuccess: () => refreshAccess(qc),
  });
}

export function usePatchRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; updated_at: string; body: RolePatch }) =>
      api.patch<AuthzRole>(`/authz/roles/${vars.id}`, vars.body, vars.updated_at),
    onSuccess: () => refreshAccess(qc),
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: boolean }>(`/authz/roles/${id}`),
    onSuccess: () => refreshAccess(qc),
  });
}

/**
 * Replace a role's grants. `updated_at` goes as If-Match: a role edited by someone else since it was
 * loaded is refused with 412, so their change is not overwritten.
 */
export function usePutGrants() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; updated_at: string; grants: Grant[] }) =>
      api.put<AuthzRole>(`/authz/roles/${vars.id}/grants`, vars.grants, vars.updated_at),
    onSuccess: () => refreshAccess(qc),
  });
}

export function useRoleHolders(id: string | null) {
  return useQuery({
    queryKey: ['authz', 'role-holders', id],
    queryFn: () => api.get<RoleHolder[]>(`/authz/roles/${id}/holders`),
    enabled: !!id,
  });
}

// ── Assignments ────────────────────────────────────────────────────────────

export function useAssignments(filter: AssignmentFilter, enabled = true) {
  return useQuery({
    queryKey: ['authz', 'assignments', filter],
    queryFn: () => api.get<Assignment[]>('/authz/assignments', { ...filter, limit: 500 }),
    enabled,
  });
}

export function useCreateAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AssignmentCreate) => api.post<Assignment>('/authz/assignments', body),
    onSuccess: () => refreshAccess(qc),
  });
}

export function useDeleteAssignment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ deleted: boolean; warning?: string }>(`/authz/assignments/${id}`),
    onSuccess: () => refreshAccess(qc),
  });
}

// ── Catalog, effective access, explain, audit ──────────────────────────────

/** `enabled`: false for a reader without authz.role.view, who is refused the catalog. */
export function useCatalog(enabled = true) {
  return useQuery({
    queryKey: ['authz', 'catalog'],
    queryFn: () => api.get<CatalogModule[]>('/authz/permissions'),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/** The verbs change only with a release. */
export function useVerbs() {
  return useQuery({
    queryKey: ['authz', 'verbs'],
    queryFn: () => api.get<Verb[]>('/authz/verbs'),
    staleTime: 60 * 60 * 1000,
  });
}

export function effectiveQuery(userId: string) {
  return queryOptions({
    queryKey: ['authz', 'effective', userId],
    queryFn: () => api.get<Effective>(`/authz/effective/${userId}`),
  });
}

export function useEffective(userId: string | null) {
  return useQuery({ ...effectiveQuery(userId ?? ''), enabled: !!userId });
}

export function useExplain(userId: string | null, key: string | null) {
  return useQuery({
    queryKey: ['authz', 'explain', userId, key],
    queryFn: () => api.get<Explain>('/authz/explain', { user_id: userId, key }),
    enabled: !!userId && !!key,
  });
}

/** The previous page stays on screen while the next loads, so the pager keeps its place and focus. */
export function useAudit(params: AuditParams) {
  return useQuery({
    queryKey: ['authz', 'audit', params],
    queryFn: () => api.get<{ items: AuditItem[]; total: number }>('/authz/audit', params),
    placeholderData: keepPreviousData,
  });
}
